import styled from "@emotion/styled";
import { MDSPanelWidths } from "@/mds/foundations";
import { MDSRowItemAppearance } from "@/mds/components/lists/row-item/row-item.types";

export const StyledRowItem = styled.div<{
  $mustClickOnRowTitle: boolean;
  $isSelected: boolean | undefined;
  $isHighlighted: boolean | undefined;
  $isHovered?: boolean;
  $enableCssHover?: boolean;
  $isSpecial: boolean | undefined;
  $appearance: MDSRowItemAppearance;
  $isDisabled?: boolean;
}>`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  width: 100%;
  justify-content: flex-start;
  align-items: center;
  padding-inline: ${({ theme }) => theme.spacing["7"]};
  padding-block: ${({ theme }) => theme.spacing["4"]};
  min-height: 60px;

  cursor: ${({ $mustClickOnRowTitle }) => ($mustClickOnRowTitle ? "default" : "pointer")};

  ${({ $appearance, theme }) => {
    switch ($appearance) {
      case MDSRowItemAppearance.ActiveSuggestion:
      case MDSRowItemAppearance.UpcomingCalendarEvent:
        return `
        background-color: ${theme.colors.background.diff};
        transition: 0.2s all;
      `;
      case MDSRowItemAppearance.AudioRecordingUploading:
      case MDSRowItemAppearance.AudioRecordingProcessing:
        return `
        background-color: ${theme.colors.background.list.uploading};
        transition: 0.2s all;
      `;
      case MDSRowItemAppearance.AudioRecordingUploadingFailed:
      case MDSRowItemAppearance.AudioRecordingProcessingFailed:
        return `
        background-color: ${theme.colors.background.list.error};
        transition: 0.2s all;
      `;
      case MDSRowItemAppearance.AudioRecordingRecording:
        return `background-color: ${theme.colors.background.diff};`;

      case MDSRowItemAppearance.Standard:
        return `background-color: ${theme.colors.background.list.default};`;
    }
  }}

  border-radius: 0;
  position: relative;

  &::before {
    background-color: transparent;
    content: "";
    inset: 0 auto 0 0;
    position: absolute;
    transition: 0.2s all;
    width: 3px;
  }

  ${({ $isSpecial, theme }) =>
    $isSpecial &&
    `
    background-color: ${theme.colors.background.list.deepsearch};
    animation-name: specialFadeIn;
    animation-duration: 2200ms;
    animation-timing-function: ease-in;
    animation-delay: 1s;
    animation-iteration-count: 1;
    animation-direction: normal;
    animation-fill-mode: forwards;
  `}
  ${({ $isSelected, theme }) =>
    $isSelected &&
    `
      background-color: ${theme.colors.background.list.selected};
      transition: 0.2s all;
    `}
  ${({ $enableCssHover = true, theme }) =>
    $enableCssHover &&
    `
      &:hover {
        background-color: ${theme.colors.background.list.hover};
        /* Hack to render on top of $isSpecial animation */
        box-shadow: inset 0 0 0 9999px ${theme.colors.background.list.hover};
        transition: 0.2s all;
      }
    `}
  ${({ $isHighlighted, theme }) =>
    $isHighlighted &&
    `
      &::before {
        background-color: ${theme.colors.border.emphasis};
      }
    `}
  ${({ $isHovered, theme }) =>
    $isHovered &&
    `
      background-color: ${theme.colors.background.list.hover};
      /* Hack to render on top of $isSpecial animation */
      box-shadow: inset 0 0 0 9999px ${theme.colors.background.list.hover};
      transition: 0.2s all;
    `}

  ${({ $isDisabled }) =>
    $isDisabled &&
    `
      opacity: 0.4;
      pointer-events: none;
    `}

  user-select: none;

  @keyframes specialFadeIn {
    from {
      background-color: ${({ theme }) => theme.colors.background.list.selected};
    }
    to {
      background-color: transparent;
    }
  }

  container-type: inline-size;
  container-name: mds-row-item;

  @container mds-panel (max-width: ${MDSPanelWidths.narrow}px) {
    padding-inline: ${({ theme }) => theme.spacing["4"]};
  }
`;

export const StyledItemContent = styled.div`
  display: flex;
  flex-direction: row;
  line-height: ${({ theme }) => theme.lineHeights["text-s"]};
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const StyledRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["2"]};
  flex: 1;
  height: 100%;
  overflow: hidden;
  flex: 1 1 0%;
`;

export const StyledRow = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: flex-start;
  overflow: hidden;
  flex: 1 1 0%;
`;

export const StyledRowSeparator = styled.div`
  /* Hack to put it over the row item so it looks like the height is the same. */
  position: relative;
  margin: ${({ theme }) => `-1px ${theme.spacing["7"]} 0 ${theme.spacing["7"]}`};
  background-color: ${({ theme }) => theme.colors.border.default};
  height: 1px;
  width: calc(100% - 2 * ${({ theme }) => theme.spacing["7"]});
`;
